
import { GoogleGenAI, Modality } from "@google/genai";
import { NeuralAPIError } from "./errors";
import { safeJsonParse } from "../utils/jsonUtils";

/**
 * GSC NEURAL PROXY SERVICE - V2.1 (Strict Error Handling)
 */
export class NeuralProxy {
    
    private static getAI() {
        if (!process.env.API_KEY) {
            throw new NeuralAPIError("API_KEY_MISSING: Neural features disabled.");
        }
        return new GoogleGenAI({ apiKey: process.env.API_KEY });
    }

    public static async generateStructuredContent<T>(params: {
        model: string,
        prompt: string,
        schema: any,
        images?: string[]
    }): Promise<T> {
        try {
            const ai = this.getAI();
            // Fallback model logic
            const modelName = params.images?.length ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';

            const config: any = {
                temperature: 0.2,
                responseMimeType: "application/json",
                responseSchema: params.schema
            };

            const contents: any = [];
            if (params.images) {
                params.images.forEach(img => {
                    contents.push({ inlineData: { mimeType: 'image/jpeg', data: img } });
                });
            }
            contents.push({ text: params.prompt });

            const response = await ai.models.generateContent({
                model: modelName,
                contents: { parts: contents },
                config
            });

            return safeJsonParse<T>(response.text);
            
        } catch (error: any) {
            console.error("[NeuralProxy] Critical API Error:", error);
            throw new NeuralAPIError(error.message || "AI Generation Failed", error);
        }
    }

    public static async generateSpeech(text: string): Promise<string> {
        try {
            const ai = this.getAI();
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash-preview-tts',
                contents: [{ parts: [{ text }] }],
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: {
                        voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } }
                    }
                }
            });
            
            const audioData = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
            if (!audioData) throw new Error("No audio data in response");
            
            return audioData;
        } catch (error: any) {
            console.error("[NeuralProxy] TTS Error:", error);
            throw new NeuralAPIError("TTS Generation Failed", error);
        }
    }
}
